<template>
  <section class="bg-white py-10 md:py-20 lg:py-20 overflow-hidden">
    <div class="max-w-screen-3xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-22">
      <!-- Header Row -->
      <div
        class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 md:gap-8 mb-10"
      >
        <!-- Left: Badge + Title -->
        <div class="flex flex-col gap-6 max-w-full md:max-w-[420px]">
          <!-- Badge -->
          <UiBaseSectionBadge
            title="Nos offres d'épargne"
            :icon="IconEpargne"
            :iconSize="23"
          />

          <!-- Title -->
          <h2
            class="text-3xl lg:text-[40px] font-semibold font-outfit text-gray-800 leading-[1.2] tracking-[-0.68px] max-w-48 md:max-w-none"
          >
            Épargnez pour votre Omra & Hajj
          </h2>
        </div>

        <!-- Right: Description -->
        <p
          class="text-lg md:text-[18px] font-normal font-outfit text-gray-700/80 leading-[1.5] max-w-full md:max-w-[584px]"
        >
          Préparez sereinement votre Omra ou votre Hajj grâce à des solutions
          d'épargne conformes aux principes islamiques. Des versements
          flexibles, accessibles et transparents pour vous accompagner pas à pas
          jusqu'à l'accomplissement de votre projet spirituel.
        </p>
      </div>

      <!-- Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
        <!-- Épargne Omra Card -->
        <NuxtLink
          to="/epargne/epargne-omra"
          class="group relative h-[420px] md:h-[460px] lg:h-[550px] rounded-2xl overflow-hidden block"
        >
          <!-- Background Image -->
          <img
            src="/images/cta-epargne-omra.webp"
            alt="Épargne Omra"
            class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />

          <!-- Gradient Overlay -->
          <div
            class="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-800"
          />

          <!-- Arrow Button -->
          <div
            class="absolute right-4 md:right-6 bottom-6 md:bottom-10 w-[46px] md:w-[50px] h-[46px] md:h-[50px] rounded-full bg-gray-700 border-2 border-gray-500 flex items-center justify-center transition-all duration-300 group-hover:bg-primary group-hover:border-[#E8D2A3]"
          >
            <IconRight :size="16" class="text-white" />
          </div>

          <!-- Content -->
          <div
            class="absolute bottom-6 md:bottom-8 left-3 md:left-8 right-16 md:right-20 lg:right-24 flex flex-col gap-3 md:gap-4"
          >
            <!-- Badge -->
            <div
              class="inline-flex bg-[#fff9ec] border border-primary rounded-[38px] px-3 md:px-4 py-2 md:py-3 w-fit whitespace-normal"
            >
              <span
                class="text-[#a68235] text-[13px] md:text-[15px] font-semibold font-outfit leading-none"
              >
                Conforme aux prescriptions islamiques
              </span>
            </div>

            <!-- Title & Description -->
            <div class="flex flex-col gap-3 text-white">
              <h3
                class="text-[24px] md:text-[28px] lg:text-[32px] font-semibold font-outfit leading-[1.3] tracking-[-0.96px] capitalize"
              >
                Épargne Omra Tayssir
              </h3>

              <p
                class="text-base lg:text-lg font-normal font-outfit leading-[1.6]"
              >
                Un plan d'épargne adapté pour accomplir votre Omra.<span
                  class="md:block mt-1"
                >
                  Versements mensuels
                  <span class="font-bold">à partir de 20€.</span>
                </span>
              </p>
            </div>
          </div>
        </NuxtLink>

        <!-- Épargne Hajj Card -->
        <NuxtLink
          to="/epargne/epargne-hajj"
          class="group relative h-[420px] md:h-[460px] lg:h-[550px] rounded-2xl overflow-hidden block"
        >
          <!-- Background Image -->
          <img
            src="/images/cta-epargne-hajj.webp"
            alt="Épargne Hajj"
            class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />

          <!-- Gradient Overlay -->
          <div
            class="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-800"
          />

          <!-- Arrow Button -->
          <div
            class="absolute right-4 md:right-6 bottom-6 md:bottom-10 w-[46px] md:w-[50px] h-[46px] md:h-[50px] rounded-full bg-gray-700 border-2 border-gray-500 flex items-center justify-center transition-all duration-300 group-hover:bg-primary group-hover:border-[#E8D2A3]"
          >
            <IconRight :size="16" class="text-white" />
          </div>

          <!-- Content -->
          <div
            class="absolute bottom-6 md:bottom-8 left-3 md:left-8 right-16 md:right-20 lg:right-24 flex flex-col gap-3 md:gap-4"
          >
            <!-- Badge -->
            <div
              class="inline-flex bg-[#fff9ec] border border-primary rounded-[38px] px-3 md:px-4 py-2 md:py-3 w-fit whitespace-normal"
            >
              <span
                class="text-[#a68235] text-[13px] md:text-[15px] font-semibold font-outfit leading-none"
              >
                Conforme aux prescriptions islamiques
              </span>
            </div>

            <!-- Title & Description -->
            <div class="flex flex-col gap-3 text-white">
              <h3
                class="text-[24px] md:text-[28px] lg:text-[32px] font-semibold font-outfit leading-[1.3] tracking-[-0.96px] capitalize"
              >
                Épargne Hajj Mabrour
              </h3>
              <p
                class="text-base lg:text-lg font-normal font-outfit leading-[1.6]"
              >
                Préparez votre pèlerinage à Makkah avec notre plan d'épargne
                Hajj.<span class="md:block mt-1">
                  Versements mensuels
                  <span class="font-bold">à partir de 60€.</span>
                </span>
              </p>
            </div>
          </div>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { IconEpargne } from "~/components/icons";
import IconRight from "../icons/IconRight.vue";
</script>
